// src/pages/admin/ArticleForm.jsx
import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './ArticleForm.css';

const ArticleForm = ({ article, onClose, onSuccess }) => {
    const [activeTab, setActiveTab] = useState('TR');
    const [formData, setFormData] = useState({
        // Türkçe
        titleTR: '',
        subtitleTR: '',
        descriptionTR: '',
        contentTR: '',

        // İngilizce
        titleEN: '',
        subtitleEN: '',
        descriptionEN: '',
        contentEN: '',

        // Fransızca
        titleFR: '',
        subtitleFR: '',
        descriptionFR: '',
        contentFR: '',

        // Ortak Alanlar
        image: '',
        author: '',
        date: new Date().toISOString().split('T')[0] // YYYY-MM-DD formatı
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const token = localStorage.getItem('adminToken');

    const modules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['blockquote', 'code-block'],
            ['link'],
            ['clean']
        ],
    };

    const formats = [
        'header',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'list', 'bullet',
        'blockquote', 'code-block',
        'link'
    ];

    useEffect(() => {
        if (article) {
            setFormData({
                titleTR: article.titleTR || '',
                subtitleTR: article.subtitleTR || '',
                descriptionTR: article.descriptionTR || '',
                contentTR: article.contentTR || '',

                titleEN: article.titleEN || '',
                subtitleEN: article.subtitleEN || '',
                descriptionEN: article.descriptionEN || '',
                contentEN: article.contentEN || '',

                titleFR: article.titleFR || '',
                subtitleFR: article.subtitleFR || '',
                descriptionFR: article.descriptionFR || '',
                contentFR: article.contentFR || '',

                image: article.image || '',
                author: article.author || '',
                date: article.date || new Date().toISOString().split('T')[0]
            });
        }
    }, [article]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        setError('');
    };

    const handleContentChange = (value, lang) => {
        setFormData({
            ...formData,
            [`content${lang}`]: value
        });
        setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        if (!formData.titleTR || !formData.descriptionTR || !formData.contentTR) {
            setError('Türkçe başlık, açıklama ve içerik zorunludur!');
            setLoading(false);
            return;
        }

        try {
            const url = article
                ? `http://localhost:5211/api/article/${article.id}`
                : 'http://localhost:5211/api/article';

            const method = article ? 'PUT' : 'POST';

            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                const data = await response.json();
                alert(article ? 'Makale başarıyla güncellendi!' : 'Makale başarıyla oluşturuldu!');
                if (onSuccess) {
                    onSuccess(data);
                }
                if (onClose) {
                    onClose();
                }
            } else {
                const data = await response.json();
                setError(data.message || 'Bir hata oluştu!');
            }
        } catch (err) {
            setError('Bağlantı hatası: ' + err.message);
            console.error('Submit error:', err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="article-form">
            <div className="form-header">
                <h3>{article ? 'Makale Düzenle' : 'Yeni Makale Ekle'}</h3>
                <button onClick={onClose} className="btn-close-form">
                    <i className="fa fa-times"></i>
                </button>
            </div>

            {error && (
                <div className="form-error">
                    <i className="fa fa-exclamation-circle"></i> {error}
                </div>
            )}

            <div className="language-tabs">
                <button
                    type="button"
                    className={`tab-btn ${activeTab === 'TR' ? 'active' : ''}`}
                    onClick={() => setActiveTab('TR')}
                >
                    🇹🇷 Türkçe <span className="required-badge">Zorunlu</span>
                </button>
                <button
                    type="button"
                    className={`tab-btn ${activeTab === 'EN' ? 'active' : ''}`}
                    onClick={() => setActiveTab('EN')}
                >
                    🇬🇧 English <span className="optional-badge">Opsiyonel</span>
                </button>
                <button
                    type="button"
                    className={`tab-btn ${activeTab === 'FR' ? 'active' : ''}`}
                    onClick={() => setActiveTab('FR')}
                >
                    🇫🇷 Français <span className="optional-badge">Opsiyonel</span>
                </button>
            </div>

            <form onSubmit={handleSubmit} className="article-form-content">

                {/* Türkçe İçerik */}
                {activeTab === 'TR' && (
                    <div className="tab-content">
                        <div className="form-group">
                            <label htmlFor="titleTR">
                                Başlık (Türkçe) <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                id="titleTR"
                                name="titleTR"
                                value={formData.titleTR}
                                onChange={handleChange}
                                placeholder="Makale başlığını girin"
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="subtitleTR">Alt Başlık (Türkçe)</label>
                            <input
                                type="text"
                                id="subtitleTR"
                                name="subtitleTR"
                                value={formData.subtitleTR}
                                onChange={handleChange}
                                placeholder="Alt başlık (opsiyonel)"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="descriptionTR">
                                Açıklama (Türkçe) <span className="required">*</span>
                            </label>
                            <textarea
                                id="descriptionTR"
                                name="descriptionTR"
                                value={formData.descriptionTR}
                                onChange={handleChange}
                                placeholder="Makale açıklaması (özet)"
                                rows="3"
                                required
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="contentTR">
                                İçerik (Türkçe) <span className="required">*</span>
                            </label>
                            <ReactQuill
                                theme="snow"
                                value={formData.contentTR}
                                onChange={(value) => handleContentChange(value, 'TR')}
                                modules={modules}
                                formats={formats}
                                placeholder="Makale içeriğini buraya yazın..."
                                readOnly={loading}
                            />
                        </div>
                    </div>
                )}

                {/* İngilizce İçerik */}
                {activeTab === 'EN' && (
                    <div className="tab-content">
                        <div className="form-group">
                            <label htmlFor="titleEN">Title (English)</label>
                            <input
                                type="text"
                                id="titleEN"
                                name="titleEN"
                                value={formData.titleEN}
                                onChange={handleChange}
                                placeholder="Article title in English"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="subtitleEN">Subtitle (English)</label>
                            <input
                                type="text"
                                id="subtitleEN"
                                name="subtitleEN"
                                value={formData.subtitleEN}
                                onChange={handleChange}
                                placeholder="Subtitle (optional)"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="descriptionEN">Description (English)</label>
                            <textarea
                                id="descriptionEN"
                                name="descriptionEN"
                                value={formData.descriptionEN}
                                onChange={handleChange}
                                placeholder="Article description (summary)"
                                rows="3"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="contentEN">Content (English)</label>
                            <ReactQuill
                                theme="snow"
                                value={formData.contentEN}
                                onChange={(value) => handleContentChange(value, 'EN')}
                                modules={modules}
                                formats={formats}
                                placeholder="Write article content here..."
                                readOnly={loading}
                            />
                        </div>
                    </div>
                )}

                {/* Fransızca İçerik */}
                {activeTab === 'FR' && (
                    <div className="tab-content">
                        <div className="form-group">
                            <label htmlFor="titleFR">Titre (Français)</label>
                            <input
                                type="text"
                                id="titleFR"
                                name="titleFR"
                                value={formData.titleFR}
                                onChange={handleChange}
                                placeholder="Titre de l'article"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="subtitleFR">Sous-titre (Français)</label>
                            <input
                                type="text"
                                id="subtitleFR"
                                name="subtitleFR"
                                value={formData.subtitleFR}
                                onChange={handleChange}
                                placeholder="Sous-titre (optionnel)"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="descriptionFR">Description (Français)</label>
                            <textarea
                                id="descriptionFR"
                                name="descriptionFR"
                                value={formData.descriptionFR}
                                onChange={handleChange}
                                placeholder="Description de l'article (résumé)"
                                rows="3"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="contentFR">Contenu (Français)</label>
                            <ReactQuill
                                theme="snow"
                                value={formData.contentFR}
                                onChange={(value) => handleContentChange(value, 'FR')}
                                modules={modules}
                                formats={formats}
                                placeholder="Écrivez le contenu de l'article ici..."
                                readOnly={loading}
                            />
                        </div>
                    </div>
                )}

                {/* Ortak Alanlar - GÜNCELLENMİŞ */}
                <div className="common-fields">
                    <h4 style={{ marginTop: '30px', marginBottom: '20px', color: '#555' }}>
                        Ortak Bilgiler (Tüm Diller İçin)
                    </h4>

                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="image">Görsel URL</label>
                            <input
                                type="text"
                                id="image"
                                name="image"
                                value={formData.image}
                                onChange={handleChange}
                                placeholder="img/portfolio/01-large.jpg"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="author">Yazar</label>
                            <input
                                type="text"
                                id="author"
                                name="author"
                                value={formData.author}
                                onChange={handleChange}
                                placeholder="Av. Ahmet Yılmaz"
                                disabled={loading}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="date">
                                Tarih <span className="required">*</span>
                            </label>
                            <input
                                type="date"
                                id="date"
                                name="date"
                                value={formData.date}
                                onChange={handleChange}
                                required
                                disabled={loading}
                            />
                        </div>
                    </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-form-cancel"
                        disabled={loading}
                    >
                        İptal
                    </button>
                    <button
                        type="submit"
                        className="btn-form-submit"
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <i className="fa fa-spinner fa-spin"></i> Kaydediliyor...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-save"></i> {article ? 'Güncelle' : 'Oluştur'}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ArticleForm;